import { Button as BaseButton, message } from 'antd';
import { observer } from 'mobx-react';
import React from 'react';
import styled from 'styled-components';

export interface UploadVersionButtonProps {
  onSelected?: (file: File) => void;
}

const Container = styled(BaseButton)`
  align-items: center;
  font-size: 16px;
  padding: 0 12px;
`;

const UploadInput = styled.input.attrs({
  type: 'file',
  accept: '.json'
})`
  display: none;
`;

// Deliberately a second copy of the same predicate in Animation.tsx. Somewhere
// to collect file-type helpers is a separate question; duplicating one line is
// cheaper than picking a home for it now.
const isJsonFile = (file: File) =>
  file.type === 'application/json' || /\.json$/i.test(file.name);

function UploadVersionButton({ onSelected }: UploadVersionButtonProps) {
  const uploadInputRef = React.useRef<HTMLInputElement>(null);

  const handleUploadClick = () => {
    uploadInputRef.current?.click();
  };

  // The `accept: '.json'` on UploadInput below is only a hint to the file
  // picker -- the user can switch it to "All Files" and choose anything -- so
  // the real check lives here. It used to live nowhere: a non-JSON pick fell
  // through to a guard deep inside Animation.uploadAnimationFile, which
  // returned without uploading and without saying anything.
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (isJsonFile(file)) {
      onSelected?.(file);
    } else {
      void message.error(`${file.name} is not a .json file.`);
    }
  };

  return (
    <>
      <Container onClick={handleUploadClick}>Upload</Container>
      <UploadInput ref={uploadInputRef} onChange={handleFileChange} />
    </>
  );
}

export default observer(UploadVersionButton);
